import React, { useEffect, useState } from 'react'
import { Modal, Button, Form, Alert, Card, Row, Col } from 'react-bootstrap'
import { asrAPI } from '../../services/api'

const AsrModal = ({ show, onHide, work, onUpdate, project, workStage }) => {
  const MAX_IMAGES = 8
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [removedDocumentIds, setRemovedDocumentIds] = useState([])
  const [newDocuments, setNewDocuments] = useState([])

  const clearNewDocuments = () => {
    newDocuments.forEach((doc) => {
      if (doc.previewUrl) URL.revokeObjectURL(doc.previewUrl)
    })
    setNewDocuments([])
  }

  useEffect(() => {
    if (show) {
      setRemovedDocumentIds([])
      clearNewDocuments()
      setError(null)
    }
  }, [show, work?.id, work?.asr_record?.id])

  const handleSubmit = async (e) => {
    e.preventDefault()
    const formData = new FormData(e.target)
    newDocuments.forEach((doc) => {
      formData.append('asr_record[documents][]', doc.file)
      formData.append('asr_record[new_document_comments][]', doc.comment || '')
    })

    try {
      setLoading(true)
      if (work.asr_record) {
        await asrAPI.update(work.id, work.asr_record.id, formData)
      } else {
        await asrAPI.create(work.id, formData)
      }
      setError(null)
      onUpdate()
    } catch (err) {
      const errorMessage = err.response?.data?.errors 
        ? Object.entries(err.response.data.errors)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('\n')
        : err.response?.data?.error || 'Ошибка при сохранении АСР'
      setError(errorMessage)
      console.error('Error saving ASR:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setError(null)
    setRemovedDocumentIds([])
    clearNewDocuments()
    onHide()
  }

  const handleDownloadPdf = () => {
    if (project && workStage && work) {
      const url = `/projects/${project.id}/work_stages/${workStage.id}/project_stage_works/${work.id}/work_asr.pdf`
      window.open(url, '_blank')
    }
  }

  if (!work) return null

  const asrRecord = work.asr_record
  const visibleDocuments = (asrRecord?.documents || []).filter((doc) => !removedDocumentIds.includes(doc.id))
  const visibleImageCount = visibleDocuments.filter((doc) => doc.content_type?.startsWith('image/')).length
  const pendingImageCount = newDocuments.filter((doc) => doc.file.type?.startsWith('image/')).length
  const remainingImageSlots = Math.max(0, MAX_IMAGES - visibleImageCount - pendingImageCount)

  const handleToggleDocumentRemoval = (documentId) => {
    setRemovedDocumentIds((prev) => (
      prev.includes(documentId)
        ? prev.filter((id) => id !== documentId)
        : [...prev, documentId]
    ))
  }

  const handleDocumentsChange = (event) => {
    const files = Array.from(event.target.files || [])
    if (files.length === 0) return

    let availableImageSlots = remainingImageSlots
    const acceptedFiles = []
    let skippedImages = 0

    files.forEach((file) => {
      const isImage = file.type?.startsWith('image/')
      if (!isImage) {
        acceptedFiles.push(file)
        return
      }

      if (availableImageSlots > 0) {
        acceptedFiles.push(file)
        availableImageSlots -= 1
      } else {
        skippedImages += 1
      }
    })

    setNewDocuments((prev) => [
      ...prev,
      ...acceptedFiles.map((file, index) => ({
        id: `${Date.now()}-${index}-${file.name}`,
        file,
        comment: '',
        previewUrl: file.type?.startsWith('image/') ? URL.createObjectURL(file) : null,
      })),
    ])

    if (skippedImages > 0) {
      setError(`Можно загрузить максимум ${MAX_IMAGES} фото в АСР`)
    }
    event.target.value = ''
  }

  const handleNewDocumentCommentChange = (id, value) => {
    setNewDocuments((prev) => prev.map((doc) => (doc.id === id ? { ...doc, comment: value } : doc)))
  }

  const handleRemoveNewDocument = (id) => {
    setNewDocuments((prev) => {
      const target = prev.find((doc) => doc.id === id)
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl)
      return prev.filter((doc) => doc.id !== id)
    })
  }

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton className="bg-primary bg-opacity-10 border-0 pb-3">
        <div className="d-flex flex-column">
          <Modal.Title className="mb-1 fw-bold">
            <i className="bi bi-file-earmark-text me-2"></i>
            Акт скрытых работ
          </Modal.Title>
          <small className="text-muted">{work.work_name}</small>
        </div>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="p-4">
          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

          {asrRecord && (
            <>
              {/* Загруженные документы */}
              <div className="mb-4">
                <div className="d-flex align-items-center mb-3">
                  <i className="bi bi-images fs-5 text-primary me-2"></i>
                  <h6 className="mb-0 fw-semibold">Загруженные документы</h6>
                </div>
                {asrRecord.documents && asrRecord.documents.length > 0 ? (
                  <Row className="g-3">
                    {visibleDocuments.map((doc, index) => (
                      <Col key={doc.id || index} lg={3} md={4} sm={6}>
                        <Card className="h-100 border shadow-sm">
                          {doc.content_type?.startsWith('image/') ? (
                            <Card.Img
                              variant="top"
                              src={doc.url}
                              style={{ height: '180px', objectFit: 'cover' }}
                              loading="lazy"
                            />
                          ) : (
                            <div
                              className="card-body text-center bg-light d-flex align-items-center justify-content-center"
                              style={{ height: '180px' }}
                            >
                              <div className="text-center">
                                <i className="bi bi-file-earmark-pdf fs-1 text-danger mb-2"></i>
                                <div className="small text-muted">PDF документ</div>
                              </div>
                            </div>
                          )}
                          <Card.Body className="p-3 bg-white">
                            <div className="d-flex flex-column">
                              <small className="text-muted text-truncate mb-2" title={doc.filename}>
                                {doc.filename}
                              </small>
                              <Form.Control
                                as="textarea"
                                name={`asr_record[existing_document_comments][${doc.id}]`}
                                rows={3}
                                placeholder="Комментарий к этому файлу..."
                                defaultValue={doc.comment || ''}
                                className="mb-2"
                              />
                              <Button
                                as="a"
                                href={doc.url}
                                target="_blank"
                                variant="outline-primary"
                                size="sm"
                                className="w-100 mb-2"
                              >
                                <i className="bi bi-download me-1"></i>
                                Скачать
                              </Button>
                              {doc.id && (
                                <Button
                                  type="button"
                                  variant="outline-danger"
                                  size="sm"
                                  className="w-100"
                                  onClick={() => handleToggleDocumentRemoval(doc.id)}
                                >
                                  <i className="bi bi-trash me-1"></i>
                                  Удалить
                                </Button>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <Alert variant="light" className="border text-center py-4">
                    <i className="bi bi-file-earmark-x fs-3 text-muted d-block mb-2"></i>
                    <p className="mb-0 text-muted">Документы еще не загружены</p>
                  </Alert>
                )}
                {asrRecord.documents && asrRecord.documents.length > 0 && visibleDocuments.length === 0 && (
                  <Alert variant="warning" className="border mt-3 mb-0">
                    Все документы отмечены к удалению. Нажмите "Обновить АСР", чтобы применить изменения.
                  </Alert>
                )}
              </div>

              <div className="border-top border-2 my-4 pt-4">
                <div className="d-flex align-items-center mb-3">
                  <i className="bi bi-pencil-square fs-5 text-success me-2"></i>
                  <h6 className="mb-0 fw-semibold">Обновить АСР</h6>
                </div>
              </div>
            </>
          )}

          {/* Форма загрузки */}
          <div className="bg-light rounded-3 p-4 mb-3">
            <div className="mb-4">
              <Form.Label className="fw-semibold d-flex align-items-center mb-2">
                <i className="bi bi-cloud-upload me-2"></i>
                Загрузить файлы
              </Form.Label>
              {remainingImageSlots > 0 ? (
                <>
                  <Form.Control
                    type="file"
                    multiple
                    accept="image/*,application/pdf"
                    size="lg"
                    onChange={handleDocumentsChange}
                  />
                  <Form.Text className="mt-2">
                    <i className="bi bi-info-circle me-1"></i>
                    Поддерживаемые форматы: изображения (PNG, JPG, JPEG, WEBP) и PDF. Максимум 8 фото в одном АСР.
                  </Form.Text>
                </>
              ) : (
                <Alert variant="light" className="border mb-0">
                  Лимит в 8 фото уже достигнут. Удалите одно из фото, чтобы добавить новое.
                </Alert>
              )}
              <div className="small text-muted mt-2">
                Доступно слотов для фото: {remainingImageSlots} из {MAX_IMAGES}
              </div>
            </div>
            <div className="mb-4">
              <Form.Label className="fw-semibold d-flex align-items-center mb-2">
                <i className="bi bi-chat-left-text me-2"></i>
                Общий комментарий
              </Form.Label>
              <Form.Control
                as="textarea"
                name="asr_record[comment]"
                rows={3}
                placeholder="Общий комментарий к акту скрытых работ..."
                defaultValue={asrRecord?.comment || ''}
              />
            </div>
            {newDocuments.length > 0 && (
              <div className="mb-0">
                <Form.Label className="fw-semibold d-flex align-items-center mb-2">
                  <i className="bi bi-chat-text me-2"></i>
                  Комментарии к новым файлам
                </Form.Label>
                <Row className="g-3">
                  {newDocuments.map((doc) => (
                    <Col key={doc.id} lg={6}>
                      <Card className="h-100 border">
                        {doc.previewUrl ? (
                          <Card.Img
                            variant="top"
                            src={doc.previewUrl}
                            style={{ height: '180px', objectFit: 'cover' }}
                          />
                        ) : (
                          <div
                            className="card-body text-center bg-light d-flex align-items-center justify-content-center"
                            style={{ height: '180px' }}
                          >
                            <div className="text-center">
                              <i className="bi bi-file-earmark-pdf fs-1 text-danger mb-2"></i>
                              <div className="small text-muted">PDF документ</div>
                            </div>
                          </div>
                        )}
                        <Card.Body className="p-3">
                          <small className="text-muted d-block text-truncate mb-2" title={doc.file.name}>
                            {doc.file.name}
                          </small>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Комментарий к этому файлу..."
                            value={doc.comment}
                            onChange={(event) => handleNewDocumentCommentChange(doc.id, event.target.value)}
                          />
                          <Button
                            type="button"
                            variant="outline-danger"
                            size="sm"
                            className="w-100 mt-2"
                            onClick={() => handleRemoveNewDocument(doc.id)}
                          >
                            <i className="bi bi-trash me-1"></i>
                            Удалить из новых
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            )}
          </div>
          {removedDocumentIds.map((id) => (
            <input key={id} type="hidden" name="asr_record[remove_document_ids][]" value={id} />
          ))}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 pb-4 px-4">
          {asrRecord && project && workStage && (
            <Button variant="success" onClick={handleDownloadPdf} className="me-auto">
              <i className="bi bi-file-pdf me-2"></i>
              Скачать PDF
            </Button>
          )}
          <Button variant="outline-secondary" onClick={handleClose}>
            <i className="bi bi-x-lg me-2"></i>
            Закрыть
          </Button>
          <Button variant="primary" type="submit" disabled={loading} className="px-4">
            <i className="bi bi-check-lg me-2"></i>
            {asrRecord ? 'Обновить АСР' : 'Сохранить АСР'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default AsrModal
